/* eslint-disable react/no-this-in-sfc */
import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { numbers } from '@annexus/utils';
import { useMediaQuery } from '@mui/material';
import variables from '../../styles/variables.module.scss';
import RedLine from '../../assets/images/lineRed.svg';
import GreenLine from '../../assets/images/lineDarkGreen.svg';
import './styles.scss';
import roundedCorners from './RoundedCorners';
import { useAppContext } from '../../context/appContext';
import { thousandsFormatter } from '../../utils';

roundedCorners(Highcharts);
const SSBenefitsChart = () => {
  const mobileScreen = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const mobileFontSize = mobileScreen ? '24px' : '30px';
  const {
    store: {
      beforeReductionColumn,
      afterReductionFirstColumn,
      afterReductionSecondColumn,
    },
  } = useAppContext();

  let highchartOptions = Highcharts.getOptions();
  const drawCustomLabels = (chart) => {
    let extraMargin = chart.plotTop - 15;
    if (chart.chartWidth < 450) {
      extraMargin = chart.plotTop - 25;
    }
    const redY = chart.series[1].points[1].dataLabels[0].y + extraMargin;
    const greenY = chart.series[2].points[1].dataLabels[0].y + extraMargin;
    const labels = document.querySelectorAll('.custom-label-container');
    let extraXMargin = 60;

    if (chart.chartWidth < 651) {
      extraXMargin = 45;
    }

    if (chart.chartWidth < 602) {
      extraXMargin = 30;
    }

    if (chart.chartWidth < 450) {
      extraXMargin = 20;
    }
    const Xposition = chart.series[2].points[1].plotX + chart.series[2].barW - extraXMargin;
    if (labels) {
      labels.forEach((element) => element?.parentNode && element.parentNode.removeChild(element));
    }
    if ((chart?.series[1]?.points[1]?.dlBox?.height || chart?.series[1]?.points[1]?.shapeArgs?.height) > 40) {
      chart.renderer
        .text(
          `<div class='red-label custom-label-container'>
              <img src='${RedLine}' aria-label="label"></img>
              <div class='custom-label-text'>Lifetime Benefit Reduction</div>
          </div>`,
          Xposition,
          redY,
          true,
        )
        .attr({
          zIndex: 90,
          useHTML: true,
        })
        .add();
    } else {
      chart.renderer
        .text(
          `<div class='red-label custom-label-container'>
              <img src='${RedLine}' aria-label="label" class="custom-position"></img>
              <div class='custom-label-text custom-position'>Lifetime Benefit Reduction</div>
          </div>`,
          Xposition,
          redY,
          true,
        )
        .attr({
          zIndex: 90,
          useHTML: true,
        })
        .add();
      if ((chart?.series[1]?.points[1]?.dlBox?.height || chart?.series[1]?.points[1]?.shapeArgs?.height) > 40) {
        chart.renderer
          .text(
            `<div class='red-label custom-label-container'>
              <div class='custom-label-text value custom-position'> $${numbers.formatNumberWithDecimals(chart?.series[1]?.points[1]?.y)}</div>
          </div>`,
            Xposition,
            redY,
            true,
          )
          .attr({
            zIndex: 90,
            useHTML: true,
          })
          .add();
      } else {
        chart.renderer
          .text(
            `<div class='red-label custom-label-container'>
              <div class='custom-label-text value custom-position-txt'> $${numbers.formatNumberWithDecimals(chart?.series[1]?.points[1]?.y)}</div>
          </div>`,
            Xposition,
            redY,
            true,
          )
          .attr({
            zIndex: 90,
            useHTML: true,
          })
          .add();
      }
    }
    chart.renderer
      .text(
        `<div class='green-label custom-label-container'>
            <img src='${GreenLine}' aria-label="label"></img>
            <span class='custom-label-text'>Remaining benefits after reduction</span>
          </div>`,
        Xposition,
        greenY,
        true,
      )
      .attr({
        zIndex: 90,
      })
      .add();
  };

  const getDataSeries = () => [
    {
      data: [...afterReductionFirstColumn],
      showInLegend: false,
      borderRadius: 5,
    },
    {
      data: [...beforeReductionColumn],
      showInLegend: false,
      borderRadiusTopLeft: 5,
      borderRadiusTopRight: 5,
    },
    {
      data: [...afterReductionSecondColumn],
      borderRadiusBottomLeft: 5,
      borderRadiusBottomRight: 5,
      showInLegend: false,
    },
  ];

  const roundTicksValues = (tick) => {
    if (tick > 10000000) {
      return Math.round(tick / 1000000) * 1000000;
    }
    if (tick > 1000000) {
      return Math.round(tick / 100000) * 100000;
    }
    if (tick > 100000) {
      return Math.round(tick / 10000) * 10000;
    }
    if (tick > 1000) {
      return Math.round(tick / 100) * 100;
    }
    return tick;
  };

  highchartOptions = {
    chart: {
      events: {
        load() {
          drawCustomLabels(this);
        },
        redraw() {
          drawCustomLabels(this);
        },
      },
      backgroundColor: variables.ssBenefitsBackground,
      height: 550,
      marginBottom: 90,
      marginLeft: 120,
      renderTo: 'container',
      spacingRight: 30,
      spacingTop: 50,
      animation: false,
      style: {
        fontFamily: variables.fontMedium,
      },
      type: 'column',
    },
    credits: {
      enabled: false,
    },
    lang: {
      thousandsSep: ',',
    },
    legend: false,
    colors: [
      variables.totalBenefitsReductionColor,
      variables.totalBenefitsAfterReductionColor,
      variables.totalBenefitsReductionColor,
    ],
    plotOptions: {
      column: {
        stacking: 'normal',
        borderWidth: 0,
        pointPadding: 0,
        pointWidth: 200,
        dataLabels: {
          enabled: true,
          useHTML: true,
          formatter() {
            if ((this.point?.shapeArgs?.height || this.point?.dlBox?.height) < 41 || this.y === 0) {
              return '';
            }
            return `$${numbers.formatNumberWithDecimals(this.y)}`;
          },
          style: {
            fontFamily: variables.fontSemiBold,
            fontSize: mobileFontSize,
            textAlign: 'center',
          },
          verticalAlign: 'middle',
        },
      },
      series: {
        enableMouseTracking: false,
        marker: false,
        states: {
          hover: {
            enabled: false,
          },
        },
      },
    },
    title: {
      text: '',
    },
    tooltip: {
      enabled: false,
    },
    xAxis: {
      categories: [
        'Total Benefits Before Reduction',
        'Total Benefits After Reduction',
      ],
      labels: {
        useHTML: true,
        style: {
          color: variables.ssBenefitsChartXLabelsColor,
          fontFamily: variables.fontSemiBold,
          fontSize: '20px',
          marginBottom: '-10px',
          paddingRight: '20px',
          paddingLeft: '20px',
          textAlign: 'center',
          width: 150,
        },
      },
      useHTML: true,
    },
    yAxis: {
      labels: {
        formatter() {
          return `$${numbers.formatNumberWithDecimals(this.value)}`;
        },
        style: {
          width: 200,
          color: variables.ssBenefitsChartYLabelsColor,
          fontFamily: variables.fontLight,
          fontSize: '16px',
          textAlign: 'right',
        },
      },
      min: 0,
      title: {
        text: '',
      },
      tickPositioner() {
        if (afterReductionFirstColumn[0] > 2) {
          const minData = Math.round(afterReductionFirstColumn[0] / 4);
          const maxData = afterReductionFirstColumn[0];
          const ticksPos = [0];
          let tick = minData;
          for (minData; tick <= maxData; tick += minData) {
            ticksPos.push(roundTicksValues(tick));
          }
          ticksPos.push(roundTicksValues(maxData + minData));
          return ticksPos;
        }
        return [0, 1, 2, 3];
      },
    },
    series: getDataSeries(),
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 850,
          },
          chartOptions: {
            chart: {
              marginLeft: 80,
            },
            plotOptions: {
              column: {
                pointWidth: 180,
              },
            },
            yAxis: {
              labels: {
                formatter() {
                  return `$${numbers.formatNumberWithDecimals(this.pos)}`;
                },
              },
            },
          },
        },
        {
          condition: {
            maxWidth: 650,
          },
          chartOptions: {
            plotOptions: {
              column: {
                pointWidth: 150,
                pointPadding: 0,
              },
            },
            yAxis: {
              labels: {
                formatter() {
                  return `$${thousandsFormatter(this.pos)}`;
                },
              },
            },
          },
        },
        {
          condition: {
            maxWidth: 450,
          },
          chartOptions: {
            chart: {
              height: 500,
              marginLeft: 60,
              spacingRight: 0,
            },
            plotOptions: {
              column: {
                pointWidth: 90,
                dataLabels: {
                  style: {
                    fontSize: '13px',
                  },
                  y: 15,
                },
              },
            },
            xAxis: {
              categories: [
                'Estimated Social Security benefits <br> at risk',
                'Estimated Social Security benefits <br> after reduction',
              ],
              labels: {
                useHTML: true,
                allowOverlap: true,
                style: {
                  fontSize: '14px',
                  paddingRight: 0,
                  paddingLeft: 0,
                },
              },
            },
            yAxis: {
              labels: {
                style: {
                  fontSize: '13px',
                },
              },
            },
          },
        },
        {
          condition: {
            maxWidth: 330,
          },
          chartOptions: {
            chart: {
              height: 400,
            },
            plotOptions: {
              column: {
                pointWidth: 80,
              },
            },
            xAxis: {
              labels: {
                useHTML: true,
                allowOverlap: true,
              },
            },
          },
        },
      ],
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={highchartOptions} />;
};

export default SSBenefitsChart;
